import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Gem, 
  Globe, 
  Shield, 
  Award,
  Users,
  Clock,
  Heart
} from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WhyChooseUs: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();

  const features = [
    {
      icon: Target,
      title: 'Expert Planning',
      description: 'Meticulously crafted itineraries by travel experts with years of experience in creating unforgettable journeys.',
      color: 'text-primary-deep-blue',
      bgColor: 'bg-primary-deep-blue bg-opacity-10',
    },
    {
      icon: Gem,
      title: 'Premium Quality',
      description: 'Handpicked accommodations and experiences that exceed expectations, ensuring luxury at every step.',
      color: 'text-primary-royal-purple',
      bgColor: 'bg-primary-royal-purple bg-opacity-10',
    },
    {
      icon: Globe,
      title: 'Global Network',
      description: 'Extensive network of trusted partners worldwide ensuring seamless travel experiences across all destinations.',
      color: 'text-primary-emerald',
      bgColor: 'bg-primary-emerald bg-opacity-10',
    },
    {
      icon: Shield,
      title: '24/7 Support',
      description: 'Round-the-clock assistance and support throughout your journey, ensuring peace of mind wherever you are.',
      color: 'text-accent-rose-gold',
      bgColor: 'bg-accent-rose-gold bg-opacity-10',
    },
    {
      icon: Award,
      title: 'Award Winning',
      description: 'Recognized for excellence in travel services with numerous industry awards and certifications.',
      color: 'text-primary-gold',
      bgColor: 'bg-primary-gold bg-opacity-10',
    },
    {
      icon: Users,
      title: 'Small Groups',
      description: 'Intimate group sizes for personalized attention and authentic local experiences with fellow travelers.',
      color: 'text-secondary-slate',
      bgColor: 'bg-secondary-slate bg-opacity-10',
    },
    {
      icon: Clock,
      title: 'Flexible Booking',
      description: 'Easy booking process with flexible payment options and hassle-free cancellation policies.',
      color: 'text-info',
      bgColor: 'bg-info bg-opacity-10',
    },
    {
      icon: Heart,
      title: 'Memorable Moments',
      description: 'Focus on creating lasting memories with unique experiences that connect you with local cultures.',
      color: 'text-error',
      bgColor: 'bg-error bg-opacity-10',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section ref={ref} className="py-2xl md:py-3xl bg-neutral-white">
      <div className="container">
        <motion.div
          className="text-center mb-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-rose-gold bg-opacity-10 rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <Award size={20} className="text-accent-rose-gold" />
            <span className="body-small font-semibold text-accent-rose-gold">
              Why Choose RAISE Tours
            </span>
          </motion.div>

          <motion.h2
            className="heading-primary text-neutral-charcoal mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Crafting Extraordinary Travel Experiences
          </motion.h2>

          <motion.p
            className="body-large text-secondary-slate max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            With over a decade of experience in luxury travel, we've perfected the art of 
            creating unforgettable journeys that exceed expectations and create memories that last a lifetime.
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4 -mx-2 px-2 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8 md:mx-0 md:px-0 md:overflow-visible"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className="group snap-start shrink-0 w-[60%] sm:w-[42%] md:w-auto"
            >
              <div className="h-full text-center p-3 md:p-6 rounded-2xl hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border border-neutral-medium-gray hover:border-primary-deep-blue hover:border-opacity-30">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 ${feature.bgColor} rounded-xl mb-3 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon size={24} className={`md:w-7 md:h-7 ${feature.color}`} />
                </div>

                {/* Title */}
                <h3 className="text-sm md:text-base font-semibold text-neutral-charcoal mb-2 md:mb-4 group-hover:text-primary-deep-blue transition-colors">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-xs md:text-sm text-secondary-slate leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
